/**
 * Starters: what the Chief of Staff can pick up from the workspace right now, offered on an empty conversation. Each
 * one is something its tools can actually do, or a way to where the work is; quiet workspaces get the standard examples.
 */
import type { AgentRunSummary, Conversation, PlanApproval } from '@diggens/types';
import type { FundSubmissionStatus } from '@diggens/shared-hooks';
import type { Starter } from '@diggens/chat';
import type { ExamplePrompt } from './examplePrompts';

export type { Starter, StarterAction } from '@diggens/chat';

export interface CollectionSummary {
  period: string;
  /** Companies with no submission for the period, or only a draft: what the Chief of Staff counts as pending. */
  notReported: number;
  needsReview: number;
  completed: number;
}

/** A period's submissions across the funds that report on it, counted the way the Chief of Staff's status tool counts them. */
export function summarizeCollection(period: string, rows: Array<Pick<FundSubmissionStatus, 'status'>>): CollectionSummary {
  return {
    period,
    notReported: rows.filter((row) => !row.status || row.status === 'draft').length,
    needsReview: rows.filter((row) => row.status === 'needs_review').length,
    completed: rows.filter((row) => row.status === 'completed').length,
  };
}

export interface StarterInput {
  /** The person looking at the chat. "Try again" sends a request as their own message, so it is offered only for theirs. */
  viewerId: string | null;
  pendingPlans: Array<Pick<PlanApproval, 'planId' | 'goal'>>;
  /** Runs of the last 7 days that failed, newest first. */
  failedRuns: Array<Pick<AgentRunSummary, 'runId' | 'title' | 'agent' | 'trigger' | 'userId'>>;
  collection: CollectionSummary | null;
  /** The newest conversation, if any. */
  lastConversation: Pick<Conversation, 'id' | 'title'> | null;
  examples: ExamplePrompt[];
}

/** Starters shown on an empty conversation. */
export const STARTERS_SHOWN = 3;

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
const QUARTER = /^Q[1-4] \d{4}$/;

/**
 * The starters for an empty conversation, most pressing first: a plan waiting on the user, a run that failed, the period's
 * collection, and the last conversation; the standard examples fill what is left.
 */
export function buildStarters({ viewerId, pendingPlans, failedRuns, collection, lastConversation, examples }: StarterInput): Starter[] {
  const live: Starter[] = [];
  const plan = pendingPlans[0];
  if (plan) {
    const more = pendingPlans.length > 1 ? ` (and ${plural(pendingPlans.length - 1, 'other', 'others')})` : '';
    live.push({ key: `plan-${plan.planId}`, tag: 'Waiting on you', tone: 'warning', text: `Review the plan to ${lowerFirst(plan.goal)}${more}`, action: { kind: 'review', planId: plan.planId } });
  }
  const failed = failedRuns[0];
  if (failed) {
    // The Chief of Staff can redo its own requests and a specialist's job, by planning it again; an extraction or a CRM
    // action it cannot, so those open the run to see why it failed. Trying again sends the request as the viewer's own
    // message, so a colleague's request or job opens in Activity too, rather than being run as the viewer.
    live.push(
      failed.userId !== null && failed.userId === viewerId && (failed.agent === 'manager' || failed.trigger === 'job')
        ? { key: `failed-${failed.runId}`, tag: 'Failed run', tone: 'error', text: `Try again: ${failed.title}`, action: { kind: 'send', prompt: `Please try this again: ${failed.title}` } }
        : { key: `failed-${failed.runId}`, tag: 'Failed run', tone: 'error', text: `See why it failed: ${failed.title}`, action: { kind: 'run', runId: failed.runId } }
    );
  }
  if (collection && collection.notReported > 0) {
    const { period, notReported } = collection;
    live.push({
      key: 'not-reported',
      tag: period,
      tone: 'info',
      text: `${plural(notReported, "company hasn't", "companies haven't")} reported for ${period}`,
      action: { kind: 'send', prompt: `Which companies haven't reported for ${period} yet?` },
    });
  }
  if (collection && collection.needsReview > 0) {
    const { period, needsReview } = collection;
    live.push({
      key: 'needs-review',
      tag: 'Needs review',
      tone: 'warning',
      text: `${plural(needsReview, `${period} submission needs`, `${period} submissions need`)} review`,
      action: { kind: 'send', prompt: `Show me the ${period} submissions that need review.` },
    });
  }
  if (lastConversation) {
    live.push({
      key: `continue-${lastConversation.id}`,
      tag: 'Continue',
      tone: 'neutral',
      text: lastConversation.title || 'Your last conversation',
      action: { kind: 'open', conversationId: lastConversation.id },
    });
  }
  // The anomaly check compares quarters, so only a quarterly period gets it
  if (collection && collection.completed > 0 && QUARTER.test(collection.period)) {
    const { period, completed } = collection;
    live.push({
      key: 'anomalies',
      tag: period,
      tone: 'info',
      text: `Check the ${plural(completed, 'completed submission', 'completed submissions')} for unusual numbers`,
      action: { kind: 'send', prompt: `Check the ${period} submissions for anomalies against the prior quarter.` },
    });
  }

  const starters = live.slice(0, STARTERS_SHOWN);
  // A live collection starter already covers the collection example
  const fillers = examples.filter((example) => !(collection && example.title === 'Collection status' && starters.some((starter) => starter.tag === collection.period)));
  for (const example of fillers) {
    if (starters.length >= STARTERS_SHOWN) break;
    starters.push({ key: `example-${example.title}`, tag: null, tone: 'neutral', text: example.prompt, action: { kind: 'send', prompt: example.prompt } });
  }
  return starters;
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}
